/* eslint-disable import/no-unused-modules */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useRevalidator } from '@remix-run/react';

export type StopReason = 'timeout' | 'conditionFulfil';

/**
 * Hook that provides auto reload functionality with custom stop conditions
 *
 * @returns Object containing auto reload state and control functions
 */
export const useAutoReload = <T = unknown>() => {
    const revalidator = useRevalidator();

    // Refs for managing intervals and timeouts
    const autoReloadIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const autoReloadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const autoReloadCountRef = useRef<number>(0);
    const previousDataRef = useRef<unknown>(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const canStopRef = useRef<
        ((currentData: T, previousData: T) => boolean) | null
    >(null);
    const onStopRef = useRef<
        | ((stopReason: StopReason, currentData: T, previousData: T) => void)
        | null
    >(null);

    // State for tracking auto reload status
    const [isAutoReloading, setIsAutoReloading] = useState(false);

    /**
     * Clears any existing auto reload intervals and timeouts
     */
    const clearAutoReload = useCallback(() => {
        if (autoReloadIntervalRef.current) {
            clearInterval(autoReloadIntervalRef.current);
            autoReloadIntervalRef.current = null;
        }
        if (autoReloadTimeoutRef.current) {
            clearTimeout(autoReloadTimeoutRef.current);
            autoReloadTimeoutRef.current = null;
        }
        autoReloadCountRef.current = 0;
        previousDataRef.current = null;
        canStopRef.current = null;
        onStopRef.current = null;
        setIsAutoReloading(false);
    }, []);

    /**
     * Enables auto reload functionality that reloads data every N seconds for a maximum duration
     * @param everyNSeconds - Interval between reloads in seconds
     * @param maxSeconds - Maximum duration to keep auto reloading in seconds
     * @param canStop - Callback function to determine if auto-reload should stop
     * @param onStop - Callback function called when auto reload stops
     */
    const enableAutoReload = useCallback(
        (
            everyNSeconds: number,
            maxSeconds: number,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            canStop?: (currentData: T, previousData: T) => boolean,
            onStop?: (
                stopReason: StopReason,
                currentData: T,
                previousData: T,
            ) => void,
        ) => {
            // Clear any existing auto reload
            clearAutoReload();

            // Store callbacks for when auto-reload should stop
            canStopRef.current = canStop || null;
            onStopRef.current = onStop || null;

            // Show loading overlay
            setIsAutoReloading(true);

            // Calculate maximum number of reloads
            const maxReloads = Math.floor(maxSeconds / everyNSeconds);
            autoReloadCountRef.current = maxReloads;

            // Set up interval for auto reload
            autoReloadIntervalRef.current = setInterval(() => {
                if (autoReloadCountRef.current <= 0) {
                    // Call onStop callback when max reloads reached
                    if (onStopRef.current) {
                        onStopRef.current(
                            'timeout',
                            previousDataRef.current as T,
                            previousDataRef.current as T,
                        );
                    }
                    clearAutoReload();
                    return;
                }

                autoReloadCountRef.current--;

                // Trigger revalidation
                revalidator.revalidate();
            }, everyNSeconds * 1000);

            // Set up timeout to stop auto reload after maxSeconds
            autoReloadTimeoutRef.current = setTimeout(() => {
                // Call onStop callback when timeout reached
                if (onStopRef.current) {
                    onStopRef.current(
                        'timeout',
                        previousDataRef.current as T,
                        previousDataRef.current as T,
                    );
                }
                clearAutoReload();
            }, maxSeconds * 1000);
        },
        [clearAutoReload, revalidator],
    );

    /**
     * Checks if auto-reload should stop based on the current data
     *
     * @param currentData - Current data to evaluate against the stop condition
     */
    const checkForStopCondition = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (currentData: any) => {
            // Check the stop condition whenever auto-reload is active and we
            // have a previous snapshot to compare against.  We intentionally do
            // NOT gate on an interval having fired — data may arrive from any
            // revalidation source (e.g. focus-triggered) and should be evaluated
            // immediately so fast completions are caught.
            //
            // Use canStopRef.current (not a render snapshot) so the callback is
            // valid immediately after enableAutoReload in the same tick.
            if (
                autoReloadIntervalRef.current &&
                previousDataRef.current &&
                canStopRef.current &&
                canStopRef.current(currentData, previousDataRef.current as T)
            ) {
                // Call the onStop callback when stop condition is met
                onStopRef.current?.(
                    'conditionFulfil',
                    currentData,
                    previousDataRef.current as T,
                );
                clearAutoReload();
            }

            // Update the previous data reference
            previousDataRef.current = currentData;
        },
        [clearAutoReload],
    );

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            clearAutoReload();
        };
    }, [clearAutoReload]);

    return {
        isAutoReloading,
        enableAutoReload,
        clearAutoReload,
        checkForStopCondition,
    };
};
